import { useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";

type ApiSessionProbeProps = { variant?: "admin" | "agent" };

/** Calls staging Worker `/api/v1/me` with the Clerk session JWT (proves CORS + secrets). */
export function ApiSessionProbe({ variant = "agent" }: ApiSessionProbeProps) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [text, setText] = useState<string>("…");
  const base = import.meta.env.VITE_SURGEDIAL_API_BASE ?? "";

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !base) {
      setText(!base ? "Set VITE_SURGEDIAL_API_BASE" : "…");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token) {
          setText("No Clerk session token");
          return;
        }
        const r = await fetch(`${base.replace(/\/$/, "")}/api/v1/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await r.text();
        if (!cancelled) {
          setText(`${r.status} ${r.statusText}\n${body}`);
        }
      } catch (e) {
        if (!cancelled) {
          setText(String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken, base]);

  if (!isSignedIn || !base) {
    return null;
  }

  const preBg =
    variant === "admin"
      ? "var(--sd-surface, #131820)"
      : "#f0ebe3";
  const titleColor = variant === "admin" ? "var(--sd-muted, #8b95a8)" : "var(--sdw-muted, #78716c)";

  return (
    <section style={{ marginTop: variant === "admin" ? "1.5rem" : 0 }}>
      <h2 style={{ fontSize: "0.85rem", fontWeight: 600, color: titleColor, marginBottom: "0.5rem" }}>
        Worker /api/v1/me
      </h2>
      <pre
        style={{
          fontSize: "0.75rem",
          overflow: "auto",
          padding: "0.75rem",
          background: preBg,
          borderRadius: "8px",
          border: variant === "admin" ? "1px solid var(--sd-border, #2a3344)" : "1px solid var(--sdw-border, #e2ddd4)",
          color: variant === "admin" ? "var(--sd-text, #e6e9ef)" : "inherit",
        }}
      >
        {text}
      </pre>
    </section>
  );
}
